/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { Accordion, Table, Dropdown, Form, InputGroup, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

import { Link, useParams } from 'react-router-dom';
import { adminApi } from '../../api';

import {
  formatDateAndTime,
  capitalize,
  formatInternationalDateAndTime,
  generateFilterDate,
  toTitleCase,
} from '../../utils';
import { useAuth, useAlerts } from '../context';

import AccordionItem from '../common/AccordionItem';
import ConfirmModal from '../common/ConfirmModal';
import ProdAccessModal from './ProdAccessModal';
import TerminationDateModal from './TerminationDateModal';
import TooltipButton from '../common/TooltipButton';

function ApplicationForm({ initialValues, onSave, enabledFeatures }) {
  const { t } = useTranslation();
  const { id } = useParams();
  const [expanded, setExpanded] = useState([1]);
  const [tier, setTier] = useState({ id: null, name: initialValues.sales_tier });
  const [tiers, setTiers] = useState([]);
  const [filters, setFilters] = useState({ name: '' });
  const [currentTier, setCurrentTier] = useState({ update: false, data: undefined });
  const [openTerminationDateModal, setOpenTerminationDateModal] = useState(false);
  const [terminationDate, setTerminationDate] = useState(() => {
    if (initialValues.termination_date === undefined) return null;
    const utcDate = initialValues.termination_date;
    if (utcDate) {
      const localDate = new Date(utcDate);
      return new Date(localDate.getUTCFullYear(), localDate.getUTCMonth(), localDate.getUTCDate());
    }
    return '';
  });
  const [prodAccess, setProdAccess] = useState({
    show: false,
    access_granted: false,
    name: undefined,
    handle: undefined,
  });

  const { env } = useAuth();
  const { setAlert } = useAlerts();

  const handleExpanded = (key) =>
    setExpanded(expanded.includes(key) ? expanded.filter((e) => e !== key) : [...expanded, key]);
  const accordionItemProps = { expanded, onSetExpanded: handleExpanded };
  const accordionActiveKey = (key) => expanded.includes(key) && key;

  const filteredTiers = tiers.filter(
    (t) =>
      t.name &&
      t.name.indexOf(tier.name) === -1 &&
      (!filters.name || (filters.name && t.name.includes(filters.name)))
  );

  const updateAppSalesTier = async (values) => {
    if (values) {
      try {
        await adminApi.updateAppSalesTier(id, values);
        setAlert({
          message: t('admin.applications.detail.tier.form.messages.success'),
          variant: 'success',
        });
        onSave();
      } catch (error) {
        setAlert({ message: error, variant: 'danger' });
      } finally {
        setCurrentTier({ ...currentTier, update: false, data: undefined });
      }
    } else {
      setCurrentTier({ ...currentTier, update: false, data: undefined });
    }
  };

  const updateProdAccess = async (values) => {
    if (values && prodAccess.handle) {
      try {
        await adminApi.updateConsoleUser(prodAccess.handle, {
          access_granted: values.access_granted,
        });
        setAlert({
          message: t(
            `admin.consoleUsers.profile.prodAccess.${
              values.access_granted ? 'grant' : 'revoke'
            }.message`,
            { handle: prodAccess.handle }
          ),
          variant: 'success',
        });
        onSave();
      } catch (error) {
        setAlert({ message: error, variant: 'warning' });
      } finally {
        setProdAccess({ ...prodAccess, show: false });
      }
    } else {
      setProdAccess({ ...prodAccess, show: false });
    }
  };

  const handleDisplayLimits = (app) => {
    if (!app.limits) return <span className="text-info">{t('common:labels.na')}</span>;
    const limitTypes = Object.keys(app.limits);
    if (!limitTypes.length) {
      return <span className="text-info">{t('admin.applications.detail.labels.noLimits')}</span>;
    }
    return limitTypes.map((type, index) => {
      const { amount, uuid, duration } = app.limits[type];
      let descriptionStr;
      if (type === 'aggregate') {
        descriptionStr = `Dollar amount allowed per ${duration / 60 / 60 / 24} days = $${(
          amount / 100
        ).toFixed(2)}`;
      } else if (type === 'rate') {
        descriptionStr = `Transactions allowed per ${duration / 60 / 60 / 24} days = ${amount}`;
      }
      return (
        <div
          className={`d-flex align-items-center${index === limitTypes.length - 1 ? '' : ' mb-1'}`}
          key={uuid}
        >
          <Link to={`/admin/limit/${uuid}`} className="text-reset text-underline p-0">
            {descriptionStr}
          </Link>
        </div>
      );
    });
  };

  const handleModifyTerminationDate = async ({ termination_date }) => {
    let termDate = termination_date;
    if (termination_date === '') {
      termDate = null;
    } else {
      const dateObj = new Date(termination_date);
      termDate = `${dateObj.getUTCFullYear()}-${dateObj.getUTCMonth() + 1}-${dateObj.getUTCDate()}`;
    }
    try {
      await adminApi.updateAppTerminationDate(id, termDate);
      setAlert({
        message: t('admin.applications.detail.terminationDate.success', {
          handle: initialValues.handle,
        }),
        variant: 'success',
      });
      setTerminationDate(termDate);
      setOpenTerminationDateModal(false);
    } catch (error) {
      setAlert({ message: error, variant: 'danger' });
    }
  };

  useEffect(() => {
    const fetchSalesTiers = async () => {
      try {
        const response = await adminApi.fetchSalesTiers({ limit: 100 });
        setTiers(response.sales_tiers);
      } catch (error) {
        setAlert({ message: error, variant: 'danger' });
      }
    };
    fetchSalesTiers();
  }, []);

  return (
    <>
      <div className="d-flex flex-column flex-md-row mb-4">
        <Dropdown
          className="sales-tier-select w-100"
          onToggle={(isVisible) => {
            if (!isVisible) setFilters({ ...filters, name: '' });
          }}
        >
          <Form.Group className="autofocus mb-0 form-floating">
            <Dropdown.Toggle className="w-100 text-start" childBsPrefix="form-control">
              <span
                className={`text-nowrap overflow-hidden${!tier.name ? ' text-info' : ''}`}
                style={{ textOverflow: 'ellipsis' }}
              >
                {tier.name ? tier.name : t('common:form.fields.select.placeholder')}
              </span>
            </Dropdown.Toggle>
            <Form.Label className="shrink">{t('applications.labels.sales_tier')}</Form.Label>
          </Form.Group>
          <Dropdown.Menu className="w-100">
            <InputGroup className="input-group-seamless border-bottom border-light">
              <InputGroup.Text>
                <i className="fas fa-search" />
              </InputGroup.Text>
              <Form.Control
                className="w-100"
                size="sm"
                placeholder={t('common:form.fields.search.placeholder')}
                aria-label={t('common:labels.search')}
                value={filters.name}
                onChange={(e) => setFilters({ ...filters, name: e.target.value.trim() })}
              />
              {filters.name && (
                <InputGroup.Text>
                  <Button
                    variant="link"
                    className="p-0 text-info-link"
                    onClick={() => setFilters({ ...filters, name: '' })}
                  >
                    <i className="fas fa-times-circle" />
                  </Button>
                </InputGroup.Text>
              )}
            </InputGroup>
            <div
              className="w-100 overflow-auto custom-scrollbar position-relative"
              style={{ maxHeight: 200 }}
            >
              {filteredTiers.length ? (
                filteredTiers.map((tier, index) => (
                  <Dropdown.Item
                    disabled={initialValues.deleted}
                    key={tier.name}
                    className={index !== 0 ? 'border-top border-light' : undefined}
                    onClick={() => setTier({ id: tier.id, name: tier.name })}
                  >
                    {tier.name}
                  </Dropdown.Item>
                ))
              ) : (
                <Dropdown.Item className="bg-transparent cursor-default border-top border-light text-center text-info font-italic text-sm py-3">
                  {t('common:form.messages.empty')}
                </Dropdown.Item>
              )}
            </div>
          </Dropdown.Menu>
        </Dropdown>

        <Button
          className="text-nowrap ms-auto mt-2 ms-md-4 mt-md-0"
          disabled={
            !initialValues.sales_tier ||
            (initialValues.sales_tier && initialValues.sales_tier.indexOf(tier.name) === 0)
          }
          onClick={() => setCurrentTier({ ...currentTier, update: true, data: tier })}
        >
          {t('admin.applications.detail.tier.form.buttons.save')}
        </Button>
      </div>

      <Accordion id="app" className="mb-4" activeKey={accordionActiveKey(1)}>
        <AccordionItem
          className="border rounded"
          eventKey={1}
          label={t('admin.applications.detail.headers.app')}
          {...accordionItemProps}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('common:applications.nickname')}</td>
                  <td>{initialValues.nickname}</td>
                </tr>
                <tr>
                  <td>{t('common:applications.handle')}</td>
                  <td>{initialValues.handle}</td>
                </tr>
                <tr>
                  <td>{t('common:applications.app_id')}</td>
                  <td>{initialValues.id}</td>
                </tr>
                <tr>
                  <td>{t('common:transactions.title')}</td>
                  <td>
                    <Button
                      href={`/admin/transactions?app_handle=${
                        initialValues.handle
                      }&created=${generateFilterDate(
                        new Date(),
                        -30,
                        true
                      )}&created=${generateFilterDate(new Date())}`}
                      variant="link"
                      className="p-0 text-reset text-underline"
                    >
                      {t('common:transactions.past30Days')}
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td>{t('common:end_users.label')}</td>
                  <td>
                    <Button
                      href={`/admin/end_users?app_handle=${initialValues.handle}`}
                      variant="link"
                      className="p-0 text-reset text-underline"
                    >
                      {t('common:end_users.registeredToApp')}
                    </Button>
                  </td>
                </tr>
                <tr>
                  <td>{t('common:applications.environment')}</td>
                  <td>{t(`env.${env}.label`)}</td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.brand_name')}</td>
                  <td>
                    {initialValues.brand_name ? (
                      initialValues.brand_name
                    ) : (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('common:labels.created')}</td>
                  <td>{formatDateAndTime(initialValues.created)}</td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.deleted')}</td>
                  <td>
                    {initialValues.deleted ? (
                      formatDateAndTime(initialValues.deleted)
                    ) : (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.frozen')}</td>
                  <td className="pe-4 ">
                    {initialValues.frozen ? t('common:status.true') : t('common:status.false')}
                  </td>
                </tr>
                {terminationDate !== null && (
                  <tr>
                    <td>{t('admin.applications.detail.terminationDate.label')}</td>
                    <td className={`${terminationDate.length ? '' : 'text-info'}`}>
                      {terminationDate === ''
                        ? t('common:labels.none')
                        : formatInternationalDateAndTime(terminationDate, false, 'en-US')}
                      <Button
                        onClick={() => setOpenTerminationDateModal(true)}
                        className="generate ms-4 rounded-1 py-1"
                        variant="primary"
                        size="sm"
                        disabled={
                          terminationDate &&
                          new Date(terminationDate) <=
                            new Date(new Date().setDate(new Date().getDate() - 5))
                        }
                      >
                        {t('common:labels.modify')}
                      </Button>
                    </td>
                  </tr>
                )}
                <tr>
                  <td>
                    {t('applications.labels.capabilities')}
                    <TooltipButton
                      placement="top"
                      variant="link"
                      text={t('applications.labels.capabilitiesDescription')}
                      className="ms-2 no-underline p-0"
                    >
                      <i className="sila-icon info text-lg" />
                    </TooltipButton>
                  </td>
                  <td className={`${enabledFeatures && enabledFeatures.length ? '' : 'text-info'}`}>
                    {enabledFeatures && enabledFeatures.length
                      ? enabledFeatures.map((feature) => toTitleCase(feature)).join(', ')
                      : t('common:labels.none')}
                  </td>
                </tr>
                <tr>
                  <td>{t('applications.labels.limits')}</td>
                  <td className="d-flex flex-column w-100">{handleDisplayLimits(initialValues)}</td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.client_id')}</td>
                  <td>
                    {initialValues.credentials.client_id || (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.vAccountsPrefix')}</td>
                  <td>
                    {initialValues.vaccount_prefix || (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.defaultSecCode')}</td>
                  <td>
                    {initialValues.app_sec_code || (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.acparName')}</td>
                  <td>
                    {initialValues.default_ach_company.ach_name || (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
                <tr>
                  <td>{t('admin.applications.detail.labels.approvedKYCLevels')}</td>
                  <td>
                    {initialValues.kyc_levels ? (
                      initialValues.kyc_levels.map((kyc_level) => kyc_level.name).join(', ')
                    ) : (
                      <span className="text-info">{t('common:labels.na')}</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      <Accordion id="business" className="mb-4" activeKey={accordionActiveKey(2)}>
        <AccordionItem
          className="border rounded"
          eventKey={2}
          label={t('admin.applications.detail.headers.customer')}
          {...accordionItemProps}
        >
          <div className="m-n4">
            <Table responsive className="custom-detail twoCol">
              <thead>
                <tr>
                  <th>{t('common:labels.detail')}</th>
                  <th>{t('common:labels.data')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{t('common:teams.name')}</td>
                  <td>{initialValues.team_name}</td>
                </tr>
                <tr>
                  <td>{t('common:teams.team_id')}</td>
                  <td>{initialValues.team_id}</td>
                </tr>
              </tbody>
            </Table>
          </div>
        </AccordionItem>
      </Accordion>

      {initialValues.team_members.filter((member) => member.role_name !== 'owner').length !== 0 && (
        <Accordion id="team-members" className="mb-4" activeKey={accordionActiveKey(3)}>
          <AccordionItem
            className="border rounded"
            eventKey={3}
            label={t('admin.applications.detail.headers.teamMembers')}
            {...accordionItemProps}
          >
            <div className="m-n4">
              <Table responsive className="custom-detail">
                <thead>
                  <tr>
                    <th>{t('common:labels.name')}</th>
                    <th>{t('common:labels.email')}</th>
                    <th>{t('common:labels.phoneNumber')}</th>
                    <th>{t('common:labels.role')}</th>
                    <th>{t('admin.consoleUsers.profile.labels.productionAccess')}</th>
                  </tr>
                </thead>
                <tbody>
                  {initialValues.team_members.map((member, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>
                        <Button
                          href={`/admin/console_user/${member.login_handle}/profile`}
                          variant="link"
                          className="p-0 text-reset text-underline"
                        >
                          {member.login_name}
                        </Button>
                      </td>
                      <td
                        style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'normal' }}
                      >
                        {member.login_emails.length ? (
                          member.login_emails.map(
                            ({ email }, index) =>
                              `${email}${member.login_emails.length - 1 !== index ? ', ' : ''}`
                          )
                        ) : (
                          <span className="text-info">{t('common:labels.na')}</span>
                        )}
                      </td>
                      <td>
                        {member.login_phones.length ? (
                          member.login_phones.map(
                            ({ phone }, index) =>
                              `${phone}${member.login_phones.length - 1 !== index ? ', ' : ''}`
                          )
                        ) : (
                          <span className="text-info">{t('common:labels.na')}</span>
                        )}
                      </td>
                      <td>{capitalize(member.role_name)}</td>
                      <td>
                        <Button
                          variant="link text-underline text-nowrap"
                          onClick={() =>
                            setProdAccess({
                              ...prodAccess,
                              name: member.login_name,
                              handle: member.login_handle,
                              access_granted: member.access_granted,
                              show: true,
                            })
                          }
                          className="p-0"
                        >
                          {t(
                            `admin.consoleUsers.profile.prodAccess.${
                              member.access_granted ? 'revoke' : 'grant'
                            }.title`
                          )}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </AccordionItem>
        </Accordion>
      )}

      {currentTier.update && (
        <ConfirmModal
          show={currentTier.update}
          data={currentTier.data}
          title={t('admin.applications.detail.tier.confirm.title')}
          message={t('admin.applications.detail.tier.confirm.description', {
            name: currentTier.data.name,
          })}
          buttonLabel={t('common:buttons.save')}
          onHide={updateAppSalesTier}
        />
      )}

      {openTerminationDateModal && (
        <TerminationDateModal
          show={openTerminationDateModal}
          onSubmit={handleModifyTerminationDate}
          setShow={setOpenTerminationDateModal}
          data={initialValues}
        />
      )}

      {prodAccess.show && (
        <ProdAccessModal
          show={prodAccess.show}
          name={prodAccess.name}
          handle={prodAccess.handle}
          onHide={updateProdAccess}
          access_granted={prodAccess.access_granted}
        />
      )}
    </>
  );
}

ApplicationForm.propTypes = {
  /**
   * The values of the form
   */
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object.isRequired,
  /**
   * The onSave function to fetch data
   */
  onSave: PropTypes.func.isRequired,
};

export default ApplicationForm;
